/**
 * Display order for lookups that admins reorder by drag in the lookup table
 * (CRUDTabTableModal). A record's position is a number in its `orderField`;
 * records that were never reordered have none.
 */

/** A record's new position, to save after a drag. */
export type OrderChange = { _id: string; order: number };

/** What a lookup's CRUDA class returns from `getReorderConfig` to opt in. */
export type LookupReorderConfig = {
	/** The field that holds a record's position. */
	orderField: string;
	/** The field that names a record, for the drag handle's label. */
	nameField: string;
	/** Saves the new positions of the records that moved. */
	persistOrder: (tenantKey: string, changes: OrderChange[]) => Promise<void>;
};

const positionOf = (item: Record<string, any>, orderField: string) => {
	const value = item[orderField];
	return typeof value === "number" && Number.isFinite(value) ? value : null;
};

/**
 * Records with a position first, ascending; then those without one, in the
 * order given. Returns a new array.
 */
export const sortByDisplayOrder = <T extends Record<string, any>>(
	items: T[],
	orderField = "order"
): T[] =>
	[...items].sort((a, b) => {
		const positionA = positionOf(a, orderField);
		const positionB = positionOf(b, orderField);
		if (positionA === null) return positionB === null ? 0 : 1;
		if (positionB === null) return -1;
		return positionA - positionB;
	});

/**
 * Gives each record of a reordered list its index as its position. Returns
 * the list with those positions, and the records whose stored position
 * changed: every record on the first drag, then only the ones that moved.
 */
export const getOrderChanges = <T extends Record<string, any>>(
	reordered: T[],
	orderField = "order"
): { items: T[]; changes: OrderChange[] } => ({
	items: reordered.map((item, index) => ({ ...item, [orderField]: index })),
	changes: reordered.flatMap((item, index) =>
		positionOf(item, orderField) === index
			? []
			: [{ _id: item._id, order: index }]
	),
});
